import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Minus, Plus } from 'lucide-react';

interface GuestSelectorProps {
  value: number;
  maxGuests: number;
  onChange: (guests: number) => void;
  disabled?: boolean;
}

/**
 * GuestSelector — constrained guest picker.
 * ≤ 4 max → chip buttons. > 4 → stepper.
 */
export function GuestSelector({ value, maxGuests, onChange, disabled }: GuestSelectorProps) {
  const max = Math.max(1, maxGuests);

  if (max <= 4) {
    return (
      <div className="flex gap-2" role="radiogroup" aria-label="Antal gäster">
        {Array.from({ length: max }, (_, i) => i + 1).map(n => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={`${n} ${n === 1 ? 'gäst' : 'gäster'}`}
            disabled={disabled}
            onClick={() => onChange(n)}
            className={cn(
              "h-10 min-w-[2.75rem] px-3 rounded-md text-sm font-medium transition-colors",
              "border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              value === n
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background text-foreground border-input hover:bg-accent hover:text-accent-foreground",
              disabled && "opacity-50 pointer-events-none"
            )}
          >
            {n}
          </button>
        ))}
      </div>
    );
  }

  // Stepper for larger capacities
  return (
    <div className="flex items-center gap-3" role="group" aria-label="Antal gäster">
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-9 w-9 shrink-0"
        disabled={disabled || value <= 1}
        onClick={() => onChange(Math.max(1, value - 1))}
        aria-label="Minska antal gäster"
      >
        <Minus className="h-4 w-4" />
      </Button>
      <span className="text-sm font-medium min-w-[2rem] text-center tabular-nums">
        {value}
      </span>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-9 w-9 shrink-0"
        disabled={disabled || value >= max}
        onClick={() => onChange(Math.min(max, value + 1))}
        aria-label="Öka antal gäster"
      >
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  );
}
